import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCompany } from "../../context/CompanyContext";
import {
  House, Buildings, Users, ClipboardText, AirplaneTilt, Money, UserCirclePlus,
  Files, ChartLineUp, Megaphone, Gear, SignOut, ChartBar, ShieldCheck,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, roles: ["super_admin", "hr_admin", "manager", "employee"] },
  { to: "/companies", label: "Companies", icon: Buildings, roles: ["super_admin", "hr_admin"] },
  { to: "/employees", label: "Employees", icon: Users, roles: ["super_admin", "hr_admin", "manager"] },
  { to: "/attendance", label: "Attendance", icon: ClipboardText, roles: ["super_admin", "hr_admin", "manager", "employee"] },
  { to: "/leaves", label: "Leave", icon: AirplaneTilt, roles: ["super_admin", "hr_admin", "manager", "employee"] },
  { to: "/payroll", label: "Payroll", icon: Money, roles: ["super_admin", "hr_admin", "manager", "employee"] },
  { to: "/recruitment", label: "Recruitment", icon: UserCirclePlus, roles: ["super_admin", "hr_admin"] },
  { to: "/documents", label: "Documents", icon: Files, roles: ["super_admin", "hr_admin", "manager", "employee"] },
  { to: "/performance", label: "Performance", icon: ChartLineUp, roles: ["super_admin", "hr_admin", "manager", "employee"] },
  { to: "/announcements", label: "Announcements", icon: Megaphone, roles: ["super_admin", "hr_admin", "manager", "employee"] },
  { to: "/reports", label: "Reports", icon: ChartBar, roles: ["super_admin", "hr_admin"] },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, roles: ["super_admin", "hr_admin", "manager", "employee"] },
  { to: "/settings", label: "Settings", icon: Gear, roles: ["super_admin", "hr_admin"] },
];

export default function Sidebar({ open = true, onClose = () => {} }) {
  const { user, logout } = useAuth();
  const { activeCompany } = useCompany();
  const navigate = useNavigate();
  const role = user?.role || "employee";

  const filtered = NAV.filter((n) => n.roles.includes(role));

  return (
    <aside
      className={`fixed lg:static top-0 left-0 z-40 h-screen w-64 bg-white border-r border-border flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      data-testid="sidebar"
    >
      <div className="px-5 py-5 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#2563EB] text-white flex items-center justify-center rounded-sm">
            <ShieldCheck size={18} weight="bold" />
          </div>
          <div>
            <div className="font-display text-sm font-semibold tracking-tight text-[#0F172A]">HRMS Suite</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B]">Multi-business</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto scrollbar-thin py-2">
        {filtered.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={onClose}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "bg-[#EFF6FF] text-[#0F172A] border-[#2563EB] font-medium"
                    : "border-transparent text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-border p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] mb-2">Active Tenant</div>
        <div className="text-xs font-medium text-[#0F172A] truncate" data-testid="active-company-name">
          {activeCompany?.name || (user?.role === "super_admin" ? "All Companies" : "—")}
        </div>
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-[#475569] border border-border hover:bg-[#F1F5F9] hover:text-[#0F172A] rounded-sm transition-colors"
          data-testid="logout-button"
        >
          <SignOut size={14} /> Logout
        </button>
      </div>
    </aside>
  );
}
